import { Card, CardContent } from '@/components/ui/card';
import { Users, MessageSquareText } from 'lucide-react';

interface PollQuestionResult {
  questionId: string;
  text: string;
  type: string;
  options?: string[];
  optionCounts?: number[];
  textAnswers?: string[];
  totalResponses: number;
}

interface PollResultsData {
  pollId: string;
  title: string;
  totalResponses: number;
  questions: PollQuestionResult[];
}

interface PollResultsViewProps {
  results: PollResultsData;
}

export function PollResultsView({ results }: PollResultsViewProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Users className="h-4 w-4" />
        {results.totalResponses} {results.totalResponses === 1 ? 'response' : 'responses'} total
      </div>

      {results.questions.map((q) => (
        <Card key={q.questionId}>
          <CardContent className="pt-5 pb-5">
            <p className="font-medium mb-1">{q.text}</p>
            <p className="text-xs text-muted-foreground mb-4">
              {q.totalResponses} {q.totalResponses === 1 ? 'answer' : 'answers'}
            </p>
            {q.type === 'free_text' ? (
              <TextResults answers={q.textAnswers || []} />
            ) : (
              <ChoiceResults
                options={q.options || []}
                counts={q.optionCounts || []}
                total={q.totalResponses}
              />
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ChoiceResults({
  options,
  counts,
  total,
}: {
  options: string[];
  counts: number[];
  total: number;
}) {
  const maxCount = Math.max(...counts, 0);

  return (
    <div className="space-y-3">
      {options.map((opt, i) => {
        const count = counts[i] || 0;
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
        const isLeading = count > 0 && count === maxCount;

        return (
          <div key={i} className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className={isLeading ? 'font-medium' : ''}>{opt}</span>
              <span
                className={`tabular-nums ${isLeading ? 'font-medium text-primary' : 'text-muted-foreground'}`}
              >
                {pct}%<span className="text-muted-foreground text-xs ml-1.5">({count})</span>
              </span>
            </div>
            <div className="h-2.5 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${isLeading ? 'bg-primary' : 'bg-primary/40'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TextResults({ answers }: { answers: string[] }) {
  if (answers.length === 0) {
    return (
      <div className="flex flex-col items-center py-6 text-muted-foreground">
        <MessageSquareText className="h-8 w-8 mb-2 opacity-40" />
        <p className="text-sm">No text responses yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {answers.map((text, i) => (
        <div key={i} className="p-3 bg-muted/50 border border-border/50 rounded-lg text-sm">
          {text}
        </div>
      ))}
    </div>
  );
}
